import { useEffect, useRef } from 'react';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { UiMessage } from './types';
import ChatMessageMarkdown from './ChatMessageMarkdown';

const BOT_AVATAR = `${process.env.PUBLIC_URL || ''}/bot.png`;
const USER_AVATAR ='';

const BOT_BUBBLE = '#ffffff';
const BORDER_USER = '#D9C6FF';

type ChatMessagesProps = {
  messages: UiMessage[];
  onFollowUpClick?: (query: string) => void;
  disableFollowUps?: boolean;
};

function StreamingCursor() {
  return (
    <Box
      component="span"
      aria-hidden
      sx={{
        display: 'inline-block',
        width: '2px',
        height: '1em',
        ml: 0.25,
        verticalAlign: 'text-bottom',
        bgcolor: '#7c3aed',
        animation: 'streamCursor 1s ease-in-out infinite',
        '@keyframes streamCursor': {
          '0%, 100%': { opacity: 1 },
          '50%': { opacity: 0.2 },
        },
      }}
    />
  );
}

function TypingDots() {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        gap: 0.4,
        alignItems: 'center',
        fontWeight: 700,
        color: 'text.secondary',
        '& span': {
          animation: 'chatDot 1.2s ease-in-out infinite',
          '@keyframes chatDot': {
            '0%, 80%, 100%': { opacity: 0.25, transform: 'translateY(0)' },
            '40%': { opacity: 1, transform: 'translateY(-2px)' },
          },
        },
        '& span:nth-of-type(1)': { animationDelay: '0s' },
        '& span:nth-of-type(2)': { animationDelay: '0.2s' },
        '& span:nth-of-type(3)': { animationDelay: '0.4s' },
      }}
    >
      <span>.</span>
      <span>.</span>
      <span>.</span>
    </Box>
  );
}

const formatTableValue = (value: unknown): string => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const formatMetricDisplayValue = (value: unknown): string => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Number.isInteger(value)
      ? value.toLocaleString('en-US')
      : value.toLocaleString('en-US', { maximumFractionDigits: 4 });
  }
  return formatTableValue(value);
};

const toRenderableTable = (
  data: { columns: string[]; rows: unknown[][] } | Record<string, unknown>,
): { columns: string[]; rows: unknown[][] } => {
  if (
    'columns' in data &&
    Array.isArray(data.columns) &&
    'rows' in data &&
    Array.isArray(data.rows)
  ) {
    return {
      columns: data.columns.filter((c): c is string => typeof c === 'string'),
      rows: data.rows.filter((r): r is unknown[] => Array.isArray(r)),
    };
  }

  const objectData = data as Record<string, unknown>;
  const keys = Object.keys(objectData);
  if (keys.length === 0) return { columns: [], rows: [] };

  const allValuesAreArrays = keys.every((k) => Array.isArray(objectData[k]));
  if (allValuesAreArrays) {
    const columns = keys;
    const maxLen = keys.reduce((max, key) => {
      const arr = objectData[key] as unknown[];
      return Math.max(max, arr.length);
    }, 0);
    const rows: unknown[][] = Array.from({ length: maxLen }, (_, idx) =>
      columns.map((col) => {
        const arr = objectData[col] as unknown[];
        return arr[idx];
      }),
    );
    return { columns, rows };
  }

  return {
    columns: ['Field', 'Value'],
    rows: keys.map((key) => [key, objectData[key]]),
  };
};

export default function ChatMessages({
  messages,
  onFollowUpClick,
  disableFollowUps = false,
}: ChatMessagesProps) {
  const bottomRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages]);

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        px: 1.5,
        py: 1,
        '@keyframes fadeIn': {
          from: { opacity: 0, transform: 'translateY(4px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      {messages.map((m) => {
        if (m.kind === 'typing') {
          return (
            <Box
              key={m.id}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-end',
                gap: 1,
                py: 1.5,
                animation: 'fadeIn 0.25s ease-out',
              }}
            >
              <Avatar src={BOT_AVATAR} sx={{ width: 30, height: 30 }} />
              <Box
                sx={{
                  ml: 0.5,
                  px: 1.75,
                  py: 1.25,
                  borderRadius: '20px',
                  bgcolor: BOT_BUBBLE,
                  fontSize: '12px',
                  maxWidth: '85%',
                }}
              >
                <TypingDots />
              </Box>
            </Box>
          );
        }

        const isUser = m.role === 'user';

        if (m.kind === 'image') {
          return (
            <Box
              key={m.id}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-end',
                justifyContent: isUser ? 'flex-end' : 'flex-start',
                gap: 1,
                py: 1.5,
                animation: 'fadeIn 0.25s ease-out',
              }}
            >
              {!isUser && (
                <Avatar src={BOT_AVATAR} sx={{ width: 30, height: 30 }} />
              )}
              <Box
                component="img"
                src={m.src}
                alt={m.alt || ''}
                sx={{
                  ml: isUser ? 0 : 0.5,
                  mr: isUser ? 0.5 : 0,
                  maxWidth: 200,
                  width: '100%',
                  height: 'auto',
                  borderRadius: '20px',
                  display: 'block',
                }}
              />
              {isUser && (
                <Avatar src={USER_AVATAR} sx={{ width: 30, height: 30 }} />
              )}
            </Box>
          );
        }

        return (
          <Box
            key={m.id}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-end',
              justifyContent: isUser ? 'flex-end' : 'flex-start',
              gap: 1,
              py: 1.5,
              animation: 'fadeIn 0.25s ease-out',
            }}
          >
            {!isUser && (
              <Avatar src={BOT_AVATAR} sx={{ width: 30, height: 30 }} />
            )}
            <Box
              sx={{
                ml: isUser ? 0 : 0.5,
                mr: isUser ? 0.5 : 0,
                px: 1.75,
                py: 1.25,
                borderRadius: '20px',
                fontSize: '12px',
                lineHeight: 1.45,
                maxWidth: '85%',
                wordBreak: 'break-word',
                ...(isUser
                  ? {
                      bgcolor: '#f3e8ff',
                      border: `none`,
                      color: '#4b5563',
                    }
                  : {
                        bgcolor: m.isError ? '#ffebee' : BOT_BUBBLE,
                        border: `1px solid ${m.isError ? '#f44336' : '#d1d5db'}`, // grey border
                        boxShadow: m.isError
                          ? '0 2px 6px rgba(244, 67, 54, 0.15)'
                          : '0 2px 6px rgba(0, 0, 0, 0.08)', // light shadow
                        color: m.isError ? '#b71c1c' : '#2e1065',
                    }),
              }}
            >
              {!isUser && m.isStreaming ? (
                <Box
                  component="span"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'inherit',
                    fontSize: 'inherit',
                    lineHeight: 'inherit',
                  }}
                >
                  {m.content}
                </Box>
              ) : (
                <ChatMessageMarkdown content={m.content} />
              )}
              {!isUser && m.kind === 'text' && m.artifact?.type === 'table'
                ? (() => {
                    const artifact = m.artifact;
                    const table = toRenderableTable(artifact.data);
                    return (
                      <Box sx={{ mt: 1.25, width: '100%', maxWidth: '100%', minWidth: 0 }}>
                        {artifact.title ? (
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color: '#4c1d95',
                              mb: 0.75,
                              fontSize: {
                                xs: '0.72rem',
                                sm: '0.76rem',
                                md: '0.8rem',
                                lg: '0.84rem',
                              },
                            }}
                          >
                            {artifact.title}
                          </Typography>
                        ) : null}
                        {table.columns.length > 0 ? (
                          <TableContainer
                            sx={{
                              width: '100%',
                              maxWidth: '100%',
                              borderRadius: 0,
                              border: '1px solid #d9c6ff',
                              bgcolor: '#ffffff',
                              overflowX: 'auto',
                              WebkitOverflowScrolling: 'touch',
                              // Slight horizontal bleed on very narrow bubbles so more table fits
                              mx: { xs: -0.25, sm: 0 },
                            }}
                          >
                            <Table
                              size="small"
                              sx={{
                                width: '100%',
                                // xs: fit bubble width + wrap; sm+: allow natural width + scroll when wide
                                minWidth: { xs: '100%', sm: 420, md: 480 },
                                tableLayout: { xs: 'fixed', sm: 'auto' },
                                '& .MuiTableCell-root': {
                                  wordBreak: 'break-word',
                                  overflowWrap: 'anywhere',
                                  verticalAlign: 'top',
                                  px: { xs: 0.5, sm: 0.85, md: 1.25 },
                                },
                              }}
                            >
                              <TableHead>
                                <TableRow>
                                  {table.columns.map((col) => (
                                    <TableCell
                                      key={col}
                                      sx={{
                                        fontWeight: 700,
                                        color: '#5b21b6',
                                        bgcolor: '#f6f0ff',
                                        borderBottom: '1px solid #e9ddff',
                                        py: { xs: 0.5, sm: 0.65, md: 0.75 },
                                        whiteSpace: { xs: 'normal', sm: 'nowrap' },
                                        fontSize: {
                                          xs: '0.62rem',
                                          sm: '0.66rem',
                                          md: '0.7rem',
                                          lg: '0.74rem',
                                        },
                                      }}
                                    >
                                      {col}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {table.rows.map((row, rowIdx) => (
                                  <TableRow key={`row-${m.id}-${rowIdx}`}>
                                    {table.columns.map((_, colIdx) => (
                                      <TableCell
                                        key={`cell-${m.id}-${rowIdx}-${colIdx}`}
                                        sx={{
                                          color: '#374151',
                                          borderBottom: '1px solid #f3e8ff',
                                          py: { xs: 0.55, sm: 0.62, md: 0.7 },
                                          fontSize: {
                                            xs: '0.64rem',
                                            sm: '0.68rem',
                                            md: '0.72rem',
                                            lg: '0.76rem',
                                          },
                                        }}
                                      >
                                        {formatTableValue(row[colIdx])}
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        ) : null}
                        {artifact.footnotes?.length ? (
                          <Box sx={{ mt: 0.75 }}>
                            {artifact.footnotes.map((note, idx) => (
                              <Typography
                                key={`note-${m.id}-${idx}`}
                                sx={{
                                  color: '#6b7280',
                                  lineHeight: 1.45,
                                  fontSize: {
                                    xs: '0.62rem',
                                    sm: '0.65rem',
                                    md: '0.68rem',
                                    lg: '0.72rem',
                                  },
                                }}
                              >
                                • {note}
                              </Typography>
                            ))}
                          </Box>
                        ) : null}
                      </Box>
                    );
                  })()
                : null}
              {!isUser && m.kind === 'text' && m.artifact?.type === 'metric'
                ? (() => {
                    const artifact = m.artifact;
                    const entries = Object.entries(artifact.data).filter(
                      ([, v]) => v !== undefined,
                    );
                    if (entries.length === 0) return null;
                    return (
                      <Box sx={{ mt: 1.25, width: '100%', maxWidth: '100%' }}>
                        {artifact.title ? (
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color: '#4c1d95',
                              mb: 1,
                              fontSize: {
                                xs: '0.72rem',
                                sm: '0.76rem',
                                md: '0.77rem',
                                lg: '0.78rem',
                              },
                            }}
                          >
                            {artifact.title}
                          </Typography>
                        ) : null}
                        <Stack
                          direction="row"
                          useFlexGap
                          flexWrap="wrap"
                          spacing={1}
                          sx={{ gap: 1 }}
                        >
                          {entries.map(([label, rawValue]) => (
                            <Card
                              key={`${m.id}-metric-${label}`}
                              elevation={0}
                              sx={{
                                flex: '1 1 140px',
                                minWidth: 120,
                                maxWidth: 220,
                                borderRadius: 2,
                                border: '1px solid #d9c6ff',
                                bgcolor: '#ffffff',
                                boxShadow: '0 1px 4px rgba(124, 58, 237, 0.08)',
                              }}
                            >
                              <CardContent sx={{ py: 1.25, px: 1.5, '&:last-child': { pb: 1.25 } }}>
                                <Typography
                                  sx={{
                                    fontWeight: 600,
                                    color: '#6d28d9',
                                    textTransform: 'none',
                                    lineHeight: 1.35,
                                    mb: 0.5,
                                    fontSize: {
                                      xs: '0.62rem',
                                      sm: '0.65rem',
                                      md: '0.66rem',
                                      lg: '0.68rem',
                                    },
                                  }}
                                >
                                  {label}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontWeight: 800,
                                    color: '#2e1065',
                                    lineHeight: 1.2,
                                    letterSpacing: '-0.02em',
                                    fontSize: {
                                      xs: '0.62rem',
                                      sm: '0.65rem',
                                      md: '0.68rem',
                                      lg: '0.72rem',
                                    },
                                  }}
                                >
                                  {formatMetricDisplayValue(rawValue)}
                                </Typography>
                              </CardContent>
                            </Card>
                          ))}
                        </Stack>
                        {artifact.footnotes?.length ? (
                          <Box sx={{ mt: 0.75 }}>
                            {artifact.footnotes.map((note, idx) => (
                              <Typography
                                key={`metric-note-${m.id}-${idx}`}
                                sx={{
                                  color: '#6b7280',
                                  lineHeight: 1.45,
                                  fontSize: {
                                    xs: '0.62rem',
                                    sm: '0.65rem',
                                    md: '0.65rem',
                                    lg: '0.66rem',
                                  },
                                }}
                              >
                                • {note}
                              </Typography>
                            ))}
                          </Box>
                        ) : null}
                      </Box>
                    );
                  })()
                : null}
              {!isUser &&
              m.kind === 'text' &&
              !m.isStreaming &&
              m.followUpSuggestions?.length ? (
                <Stack
                  direction="row"
                  spacing={0.75}
                  useFlexGap
                  flexWrap="wrap"
                  sx={{ mt: 1 }}
                >
                  {m.followUpSuggestions.map((suggestion, idx) => (
                    <Chip
                      key={`${m.id}-followup-${idx}`}
                      label={suggestion}
                      size="small"
                      clickable
                      disabled={disableFollowUps}
                      onClick={() => onFollowUpClick?.(suggestion)}
                      sx={{
                        borderRadius: 1.5,
                        border: '1px solid #d9c6ff',
                        bgcolor: '#ffffff',
                        color: '#5b21b6',
                        fontWeight: 600,
                        '& .MuiChip-label': {
                          lineHeight: 1.25,
                          fontSize: {
                            xs: '0.62rem',
                            sm: '0.66rem',
                            md: '0.72rem',
                            lg: '0.76rem',
                          },
                        },
                        '&:hover': {
                          bgcolor: '#f6f0ff',
                        },
                      }}
                    />
                  ))}
                </Stack>
              ) : null}
              {!isUser && m.isStreaming ? <StreamingCursor /> : null}
            </Box>
            {isUser && (
              <Avatar src={USER_AVATAR} sx={{ width: 30, height: 30 }} />
            )}
          </Box>
        );
      })}
      <div ref={bottomRef} />
    </Box>
  );
}
